import {
  CompositeDecorator,
  ContentBlock,
  ContentState,
  EditorState,
  EntityInstance,
  Modifier,
  SelectionState,
} from 'draft-js';
import { MentionData } from './types';
import { DecoratorComponent } from './comps';
import Mexp from 'math-expression-evaluator';
import { Variable } from 'src/api/types';

const mexp = new Mexp();

export const DEFAULT_FORMULA_ERROR_MESSAGE = 'Complete the expression';
export const INVALID_SYNTAX_FORMULA_ERROR_MESSAGE = 'Invalid syntax';

/**
 * Check if a variable ID is from workspace/global source (higher ID ranges).
 * - Global variables: 500001-500999
 * - Workspace variables: 100001+ (but not in global range)
 * - Assessment variables: typically 1-99999 (lower IDs)
 */
const isWorkspaceOrGlobalId = (id: number): boolean => {
  return id >= 100001;
};

export const isWorkspaceOrGlobalVariable = (v: Variable): boolean => {
  return v.workspace_variable || v.isGlobal || isWorkspaceOrGlobalId(v.id);
};

export const customSuggestionsFilter = (search: string, suggestions: Variable[]): Variable[] => {
  const searchLower = search.toLowerCase();

  const textFiltered = suggestions.filter(
    (suggestion) =>
      (suggestion.alias && suggestion.alias.toLowerCase().includes(searchLower)) ||
      suggestion.name.toLowerCase().includes(searchLower),
  );

  // Then, filter out workspace/global variables that have overrides.
  // When an override exists (assessment variable with same name), we should only show the override.
  const filteredResults = textFiltered.filter((suggestion) => {
    if (isWorkspaceOrGlobalVariable(suggestion)) {
      const hasOverride = textFiltered.some(
        (s) => s.name === suggestion.name && s.id !== suggestion.id && s.id < suggestion.id,
      );
      if (hasOverride) {
        return false;
      }
    }
    return true;
  });

  return filteredResults;
};

const findMentionEntities = (
  contentBlock: ContentBlock,
  callback: (start: number, end: number) => void,
  contentState: ContentState,
) => {
  contentBlock.findEntityRanges((character) => {
    const entityKey = character.getEntity();
    return entityKey !== null && contentState.getEntity(entityKey).getType() === '#mention';
  }, callback);
};

export const serializeEditorStateToExpression = (editorState: EditorState) => {
  const contentState = editorState.getCurrentContent();
  let expression = '';

  contentState.getBlocksAsArray().forEach((block) => {
    let blockText = block.getText();
    let lastOffset = 0;

    block.findEntityRanges(
      (character) => {
        const entityKey = character.getEntity();
        return entityKey !== null && contentState.getEntity(entityKey).getType() === '#mention';
      },
      (start, end) => {
        const entityKey = block.getEntityAt(start);
        const entity = contentState.getEntity(entityKey) as EntityInstance;
        const mentionData = entity.getData() as MentionData;

        expression += blockText.slice(lastOffset, start) + `#${mentionData.mention.id}`;
        lastOffset = end;
      },
    );

    expression += blockText.slice(lastOffset);
    expression += ' ';
  });

  return expression.trim();
};

type InsertMention = (params: { contentState: ContentState; range: SelectionState; item: Variable }) => ContentState;

const insertMention: InsertMention = ({ contentState, range, item }): ContentState => {
  const mentionEntity = contentState.createEntity('#mention', 'SEGMENTED', {
    mention: { ...item },
  });

  const entityKey = mentionEntity.getLastCreatedEntityKey();
  return Modifier.replaceText(contentState, range, item?.alias || item?.name || '[null]', undefined, entityKey);
};

/**
 * Find a variable by ID, with override support.
 * If the variable found is a workspace/global variable AND there's an override (assessment variable)
 * with the same name, return the override instead.
 *
 * Note: Some workspace variables have incorrect flags (workspace_variable: false, isGlobal: false)
 * due to backend data issues. We detect these by:
 * 1. Checking the ID range (workspace/global IDs are >= 100001)
 * 2. Looking for duplicate names with different IDs (lower ID = override)
 */
const findVariableWithOverride = (data: Variable[], id: number): Variable | undefined => {
  const directMatch = data.find((v) => v.id === id);

  if (!directMatch) {
    return undefined;
  }

  if (isWorkspaceOrGlobalVariable(directMatch)) {
    const override = data.find((v) => v.name === directMatch.name && v.id !== directMatch.id && v.id < directMatch.id);

    if (override) {
      return override;
    }
  }

  return directMatch;
};

const parseExpressionToContentState = (expression: string, data: Variable[]): ContentState => {
  let contentState = ContentState.createFromText('');
  const regex = /#(\d+)/g;
  let lastIndex = 0;

  expression.replace(regex, (match, p1, offset) => {
    let beforeText = expression.slice(lastIndex, offset);
    lastIndex = offset + match.length;

    if (beforeText) {
      contentState = Modifier.insertText(contentState, contentState.getSelectionAfter(), beforeText);
    }

    const selection = contentState.getSelectionAfter();
    const blockKey = selection.getStartKey();
    const insertPoint = selection.getStartOffset();
    const range = selection.merge({
      anchorKey: blockKey,
      anchorOffset: insertPoint,
      focusKey: blockKey,
      focusOffset: insertPoint,
    }) as SelectionState;

    const item = findVariableWithOverride(data, Number(p1)) as Variable;

    contentState = insertMention({ contentState, range, item });
    return match;
  });

  if (lastIndex < expression.length) {
    contentState = Modifier.insertText(contentState, contentState.getSelectionAfter(), expression.slice(lastIndex));
  }

  return contentState;
};

type CreateEditorStateFromExpression = (params: { expression: string; data: Variable[] }) => EditorState;
export const createEditorStateFromExpression: CreateEditorStateFromExpression = ({ expression, data }): EditorState => {
  const mentionDecorator = new CompositeDecorator([
    {
      strategy: findMentionEntities,
      component: DecoratorComponent,
    },
  ]);

  const contentState = parseExpressionToContentState(formatExpression(expression), data);
  return EditorState.createWithContent(contentState, mentionDecorator);
};

export const formatExpression = (expression?: string | null) => {
  if (!expression) return '';

  expression = expression.replace(/\s+/g, '');
  expression = expression.replace(/(\d+(\.\d+)?|[#\d]+|\+|\-|\*|\/|\(|\))/g, ' $1 ').trim();
  expression = expression.replace(/\s+/g, ' ');

  return expression;
};

export const handleKeyCommand = (
  command: string,
  editorState: EditorState,
  setEditorState: (a: EditorState) => void,
) => {
  if (command === 'backspace') {
    const selection = editorState.getSelection();
    const contentState = editorState.getCurrentContent();
    const blockKey = selection.getStartKey();
    const block = contentState.getBlockForKey(blockKey);
    const startOffset = selection.getStartOffset();

    if (startOffset === 0) {
      return 'not-handled';
    }

    const entityKey = block.getEntityAt(startOffset - 1);

    if (entityKey) {
      const entity = contentState.getEntity(entityKey);
      if (entity.getType() === '#mention') {
        const blockText = block.getText();
        let entityRangeStart = startOffset - 1;
        let entityRangeEnd = startOffset;

        while (entityRangeStart > 0 && block.getEntityAt(entityRangeStart - 1) === entityKey) {
          entityRangeStart--;
        }
        while (entityRangeEnd < blockText.length && block.getEntityAt(entityRangeEnd) === entityKey) {
          entityRangeEnd++;
        }

        const newContentState = Modifier.removeRange(
          contentState,
          selection.merge({
            anchorOffset: entityRangeStart,
            focusOffset: entityRangeEnd,
          }),
          'backward',
        );

        const newEditorState = EditorState.push(editorState, newContentState, 'remove-range');

        setEditorState(EditorState.forceSelection(newEditorState, newContentState.getSelectionAfter()));
        return 'handled';
      }
    }
  }
  return 'not-handled';
};

export const expressionToFormula = (inputStr: string, variables: Variable[]): string => {
  let result = inputStr;

  // Build a map of variable IDs to their effective values (considering overrides)
  const idToValueMap = new Map<number, number>();

  for (const variable of variables) {
    if (isWorkspaceOrGlobalVariable(variable)) {
      const override = variables.find((v) => v.name === variable.name && v.id !== variable.id && v.id < variable.id);
      if (override) {
        idToValueMap.set(variable.id, override.value ?? 0);
      } else {
        idToValueMap.set(variable.id, variable.value ?? 0);
      }
    } else {
      idToValueMap.set(variable.id, variable.value ?? 0);
    }
  }

  for (const [id, value] of idToValueMap) {
    const regex = new RegExp(`#${id}\\b`, 'g');
    result = result.replace(regex, value.toString());
  }

  return result;
};

export const validateFormulaSyntax = (formula?: string | null) => {
  if (!formula) return true;

  if (/(^|\s|\W)\.\d+/.test(formula)) {
    return false;
  }

  try {
    mexp.lex(formula, []);
    return true;
  } catch (error) {
    return false;
  }
};

export const evaluate = async (formula: string): Promise<number> => {
  return new Promise((resolve, reject) => {
    try {
      const invalidNumberFormat = /(^|\s|\W)\.\d+/;

      if (invalidNumberFormat.test(formula)) {
        throw new Error('Invalid formula');
      }

      const val = mexp.eval(formula, [], {});

      if (isNaN(val) || val === Infinity || val === -Infinity) {
        throw new Error('Invalid formula');
      }

      const _areParenthesesBalanced = (expression: string): boolean => {
        let stack: string[] = [];

        for (let char of expression) {
          if (char === '(') {
            stack.push(char);
          } else if (char === ')') {
            if (stack.length === 0) {
              return false;
            }
            stack.pop();
          }
        }

        return stack.length === 0;
      };

      if (!_areParenthesesBalanced(formula)) {
        throw new Error('Invalid formula');
      }

      resolve(val);
    } catch (error) {
      reject(error);
    }
  });
};
